import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayPickerSingleDateController } from "react-dates";
import { createPortal } from "react-dom";
import { AppText } from "src/Components/UI";
import { useClickOutside } from "src/utils/hooks";
import { theme } from "src/utils/theme";
import styled from "styled-components";

export const MiniCalendar: React.FC<{
  onClickOutside: () => void;
  handleTimeframeSwitch: (switchTo: string) => void;
  handleDaySwitch: (day: string) => void;
  getDate: () => string;
  curView: any;
}> = ({ onClickOutside, handleTimeframeSwitch, handleDaySwitch, getDate, curView }) => {
  const ref = useRef(null);
  useClickOutside(ref, onClickOutside);

  const [date, setDate] = useState<Moment | null>(moment(getDate()));
  const selectedRow = useRef(-1);

  const findSelectedRowIndex = useCallback(() => {
    const table = document.getElementsByClassName("CalendarMonth_table")[1];
    if (table) {
      const tbody = table.querySelector("tbody");
      if (tbody) {
        const rows = tbody.querySelectorAll("tr");
        let selectedRowIndex = -1;
        rows.forEach((row, index) => {
          if (row.querySelector(".CalendarDay__selected")) {
            selectedRowIndex = index + 1; // +1 to make it 1-based index
          }
        });
        return selectedRowIndex;
      }
    }
    return -1;
  }, []);

  useEffect(() => {
    const map = ["S", "M", "T", "W", "T", "F", "S"];
    const headers = document.querySelectorAll<HTMLElement>(".DayPicker_weekHeader_li_1 small");
    headers.forEach((el, idx) => {
      el.textContent = map[idx];
    });

    const table = document.getElementsByClassName("CalendarMonth_table")[1];
    if (table) {
      // create a new WeekRow element
      const weekRowElement = document.createElement("div");
      weekRowElement.className = "WeekBacking";

      const tbody = table.querySelector("tbody");
      if (tbody) {
        tbody.appendChild(weekRowElement);
      }
    }
  }, []);

  useEffect(() => {
    const newSelectedRow = findSelectedRowIndex();
    selectedRow.current = newSelectedRow;

    const weekRow: HTMLDivElement | null = document.querySelector(".WeekBacking");
    if (weekRow) {
      // set the position of the week backing row
      weekRow.style.top = `${newSelectedRow === 1 ? 4 : (newSelectedRow - 1) * 28 + 4}px`;
    }
  }, [date]);

  const handleDateChange = useCallback(
    (date: Moment | null) => {
      setDate(date);
      handleDaySwitch(date?.format("YYYY-MM-DD") || "");
    },
    [setDate, handleDaySwitch],
  );

  const calEle = useMemo(() => document.getElementById("cal-wrap"), []);
  if (!calEle) return null;

  return createPortal(
    <MiniCalendarWrap ref={ref}>
      <TimeframeController handleTimeframeSwitch={handleTimeframeSwitch} curView={curView} />
      <DayPickerSingleDateController
        date={date}
        onDateChange={handleDateChange}
        focused
        onFocusChange={() => {}}
        initialVisibleMonth={() => (date ? date : moment())}
        numberOfMonths={1}
        hideKeyboardShortcutsPanel
        weekDayFormat="d"
      />
    </MiniCalendarWrap>,
    calEle,
  );
};

const TimeframeController: React.FC<{ handleTimeframeSwitch: (switchTo: string) => void; curView: string }> = ({
  handleTimeframeSwitch,
  curView,
}) => {
  const [isWeek, setIsWeek] = useState(curView === "timeGridWeek");
  return (
    <TimeframeContainer>
      <TimeframeButtonBacking isWeek={isWeek} />
      <TimeframeButton
        onClick={() => {
          handleTimeframeSwitch("timeGridWeek");
          setIsWeek(true);
        }}
      >
        <AppText
          fontSize={8}
          fontWeight={600}
          lineHeight={12}
          uppercase
          color={isWeek ? theme.text.neutral.inverse : theme.text.brand.primary}
        >
          Week
        </AppText>
      </TimeframeButton>
      <TimeframeButton
        onClick={() => {
          handleTimeframeSwitch("timeGridDay");
          setIsWeek(false);
        }}
      >
        <AppText
          fontSize={8}
          fontWeight={600}
          lineHeight={12}
          uppercase
          color={!isWeek ? theme.text.neutral.inverse : theme.text.brand.primary}
        >
          Day
        </AppText>
      </TimeframeButton>
    </TimeframeContainer>
  );
};

const MiniCalendarWrap = styled.div`
  position: absolute;
  z-index: 10;
  top: 50px;
  left: 20px;

  width: 280px;
  height: 212px;
  padding: 8px;

  background-color: ${theme.fill.neutral.primary};
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  animation: ${theme.lightPopup} 0.2s ease-in-out forwards;

  // remove default month nav arrows
  .DayPickerNavigation_button__default {
    display: none !important;
  }

  // weekday text
  .DayPicker_weekHeader_li_1 small {
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;

    color: ${theme.text.neutral.secondary};
  }

  // header
  .CalendarMonth_caption {
    display: none;
  }

  // week header
  .DayPicker_weekHeader {
    padding: 0px !important;
    top: 40px;
    left: 5px !important;
  }

  .WeekBacking {
    position: absolute;
    top: 4px;

    width: 258px;
    height: 24px;

    background-color: ${theme.fill.brand.secondary};
    border: 1px solid ${theme.border.brand.primary};
    border-radius: 24px;

    transition: top 0.2s ease-in-out, opacity 0.15s ease-in-out;
  }

  // day cells
  .CalendarDay {
    width: 24px !important;
    height: 24px !important;

    border: none;
    box-shadow: none;
    padding: 0;

    font-size: 16px;
    font-weight: 500;
    line-height: 22px;

    border-radius: 4px;

    background-color: transparent;

    :hover {
      background-color: ${theme.fill.brand.secondary};
      border: 1px solid ${theme.border.brand.primary};
    }

    transition: background-color 0.1s ease-in-out, border-radius 0.25s ease-in-out, border 0.25s ease-in-out;
  }
  .CalendarDay__selected {
    background-color: ${theme.fill.brand.primary} !important;
    border-radius: 100%;
  }
  .CalendarMonth_table tbody tr {
    position: relative;
    z-index: 1;
  }

  // cal wrappers
  .DayPicker {
    position: absolute;
    left: -10px;
    top: 0px;
  }
  .DayPicker__hidden {
    visibility: visible !important;
  }
  .CalendarMonth,
  .CalendarMonthGrid,
  .DayPicker__horizontal {
    background: transparent;
  }
  .CalendarMonthGrid__horizontal {
    left: 6px;
    top: 62px;
  }
  .CalendarMonth {
    padding: 0px !important;
  }
  .CalendarMonth_table {
    position: relative;
    border-collapse: separate;
    border-spacing: 15px 4px;
  }
  .DayPicker__withBorder {
    border: none;
    box-shadow: none;
    border-radius: 0px;
  }
`;

const TimeframeContainer = styled.div`
  position: relative;
  display: flex;
  padding: 2px;
  background-color: ${theme.fill.brand.secondary};
  width: fit-content;
  border-radius: 6px;
`;

const TimeframeButton = styled.button`
  position: relative;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 58px;
  height: 20px;

  cursor: pointer;

  background: transparent;
  border: none;
`;

const TimeframeButtonBacking = styled.div<{ isWeek: boolean }>`
  position: absolute;
  left: ${(props) => (props.isWeek ? "2px" : "50%")};

  width: 58px;
  height: 20px;

  border-radius: 4px;
  border: 1px solid #204fc7;
  background-color: #005fec;

  transition: left 0.15s ease-in-out;
`;
